import React, { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../../hooks/defaultHooks';
import { getTheme, modalExtra } from '../../../selectors/selectors';
import * as yup from 'yup';
import { useFormik } from 'formik';
import Button from '../../../UIComponents/Button';
import { closeModal } from '../../../store/slices/modalSlice';
import { useForgotPasswordMutation } from '../../user/user.service';
import { backendMessageOf, isFetchBaseQueryError } from '../../../utils/helpers';
import { toast } from 'react-toastify';

// the server answers the same for any address and sends the letter only to an active account,
// so the page cannot claim the letter went out
const SENT_MESSAGE = 'If this email belongs to an account, a letter is on its way';

const ForgotPassword = () => {
  const extra = useAppSelector(modalExtra);
  const validationSchema = yup
    .object()
    .strict()
    .shape({
      email: yup.string().email('Invalid email format').required('This field is required').trim(),
    });
  const theme = useAppSelector(getTheme);
  const dispatch = useAppDispatch();
  const [forgotPassword] = useForgotPasswordMutation();
  const initialValues: { email: string } = {
    email: extra?.email as string,
  };
  const [error, setError] = useState<string>('');
  const [successResponse, setSuccessResponse] = useState<boolean>(false);
  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values) => {
      const { email } = values;
      try {
        const response = await forgotPassword({ email }).unwrap();
        if (response) {
          setSuccessResponse(true);
          closeHandler();
          toast.success(SENT_MESSAGE);
        }
      } catch (error) {
        if (isFetchBaseQueryError(error)) {
          switch (error.status) {
            case 400:
              setError('Invalid request');
              toast.error('Invalid request');
              break;
            case 429: {
              // a mail limit: to this address or from this network; the server says which and how long to wait
              const message =
                backendMessageOf(error) ?? 'Too many requests, please try again later';
              setError(message);
              toast.error(message);
              break;
            }
            default:
              setError('Unknown error');
              break;
          }
        }
      }
    },
  });
  const onChangeHandle = (event: React.ChangeEvent) => {
    formik.handleChange(event);
    formik.setErrors({});
  };
  const closeHandler = () => dispatch(closeModal());
  return (
    <>
      <Modal.Header
        className="bg-dark d-flex justify-content-center text-white"
        data-testid="modal-title"
      >
        Reset Password
      </Modal.Header>
      {!successResponse ? (
        <Modal.Body className="d-flex flex-column justify-content-center align-items-center gap-3">
          <div>Enter your email below to reset your password</div>
          <Form
            data-testid="sign-in-form"
            className="d-flex flex-column justify-content-center gap-3"
            noValidate
            onSubmit={formik.handleSubmit}
          >
            {error && <div className="text-danger">{error}</div>}
            <Form.Group className="position-relative">
              <Form.Control
                type="email"
                name="email"
                id="email"
                placeholder="Email"
                onChange={onChangeHandle}
                className={`${theme}-input border-5 border-0`}
                isInvalid={!!formik.errors.email}
                value={formik.values.email}
              />
              <Form.Control.Feedback type="invalid" tooltip>
                {formik.errors.email}
              </Form.Control.Feedback>
            </Form.Group>
            <div className="d-flex gap-5">
              <Button text="Cancel" clickHandler={closeHandler} theme={theme} />
              <Button
                theme={theme}
                className={`${theme}-export-link btn`}
                type="submit"
                text="Reset Password"
              />
            </div>
          </Form>
        </Modal.Body>
      ) : (
        <Modal.Body>
          <p>{SENT_MESSAGE}</p>
          <Button text="Close" clickHandler={closeHandler} theme={theme} />
        </Modal.Body>
      )}
    </>
  );
};

export default ForgotPassword;
